import { useState, useCallback } from "react";
import { getData } from "./Utility/utility";
// ── Dummy getData ───────────────────────────────────────────────
// Replace this with your real getData(value) that returns a list of strings.


// ── Pill color palette (cycles by index) ───────────────────────
const PILL_PALETTES = [
  { bg: "#E1F5EE", text: "#0F6E56", border: "#9FE1CB" },
  { bg: "#E6F1FB", text: "#185FA5", border: "#B5D4F4" },
  { bg: "#FAEEDA", text: "#854F0B", border: "#FAC775" },
  { bg: "#FBEAF0", text: "#993556", border: "#F4C0D1" },
  { bg: "#EEEDFE", text: "#534AB7", border: "#CECBF6" },
  { bg: "#EAF3DE", text: "#3B6D11", border: "#C0DD97" },
  { bg: "#FAECE7", text: "#993C1D", border: "#F5C4B3" },
  { bg: "#FCEBEB", text: "#A32D2D", border: "#F7C1C1" },
];

const STEP = 10;

export default function NumberFactsExplorer() {
  const [rangeStart, setRangeStart] = useState(90);
  const [rangeStartInput, setRangeStartInput] = useState("90");
  const [rangeEndInput, setRangeEndInput] = useState("100");
  const [results, setResults] = useState(null); // null | Array<{ n, items }>
  const [loading, setLoading] = useState(false);

  const effectiveStart = parseInt(rangeStartInput) || 90;
  const effectiveEnd = parseInt(rangeEndInput) || 100;

  const syncInputsFromStart = (start) => {
    setRangeStartInput(String(start));
    setRangeEndInput(String(start + STEP));
  };

  const handleForward = () => {
    const next = effectiveStart + STEP;
    setRangeStart(next);
    syncInputsFromStart(next);
    setResults(null);
  };

  const handleBackward = () => {
    const prev = effectiveStart - STEP;
    setRangeStart(prev);
    syncInputsFromStart(prev);
    setResults(null);
  };

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setResults(null);

    const start = parseInt(rangeStartInput) || 90;
    const end = parseInt(rangeEndInput) || 100;

    const collected = [];
    for (let n = start + 1; n <= end; n++) {
      const items = await Promise.resolve(getData(n));
      collected.push({ n, items });
    }

    setResults(collected);
    setLoading(false);
  }, [rangeStartInput, rangeEndInput]);

  return (
    <div style={{
      fontFamily: "'Georgia', 'Times New Roman', serif",
      minHeight: "100vh",
      padding: "0 0 4rem",
      background: "var(--color-background-tertiary)",
    }}>

      {/* ── HEADER BAR ── */}
      <div style={{
        background: "var(--color-background-primary)",
        borderBottom: "0.5px solid var(--color-border-tertiary)",
        padding: "1rem 1.5rem",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px",
      }}>

        {/* Range inputs */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label style={labelStyle}>From</label>
          <input
            type="number"
            value={rangeStartInput}
            onChange={(e) => { setRangeStartInput(e.target.value); setResults(null); }}
            style={numInputStyle}
          />
          <label style={labelStyle}>to</label>
          <input
            type="number"
            value={rangeEndInput}
            onChange={(e) => { setRangeEndInput(e.target.value); setResults(null); }}
            style={numInputStyle}
          />
        </div>

        {/* Backward */}
        <button onClick={handleBackward} style={navBtnStyle} title="Shift range back">
          ← back
        </button>

        {/* Forward */}
        <button onClick={handleForward} style={navBtnStyle} title="Shift range forward">
          forward →
        </button>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            ...navBtnStyle,
            background: loading ? "var(--color-background-secondary)" : "#185FA5",
            color: loading ? "var(--color-text-secondary)" : "#fff",
            border: "0.5px solid transparent",
            fontWeight: 500,
            letterSpacing: "0.04em",
            minWidth: "90px",
          }}
        >
          {loading ? "loading…" : "Submit"}
        </button>

        {/* Range label */}
        <span style={{
          marginLeft: "auto",
          fontSize: "12px",
          letterSpacing: "0.08em",
          color: "var(--color-text-tertiary)",
          fontFamily: "'Courier New', monospace",
        }}>
          {rangeStartInput}+1 … {rangeEndInput}
        </span>
      </div>

      {/* ── RESULTS ── */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "1.5rem 1.5rem 0" }}>

        {!results && !loading && (
          <div style={{
            textAlign: "center",
            color: "var(--color-text-tertiary)",
            fontSize: "14px",
            letterSpacing: "0.06em",
            marginTop: "5rem",
          }}>
            Set a range and press Submit
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", color: "var(--color-text-secondary)", marginTop: "5rem", fontSize: "14px" }}>
            Fetching…
          </div>
        )}

        {results && results.map(({ n, items }, rowIdx) => (
          <NumberRow key={n} n={n} items={items} rowIdx={rowIdx} />
        ))}
      </div>
    </div>
  );
}

// ── Number row ────────────────────────────────────────────────
function NumberRow({ n, items, rowIdx }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: "1.25rem",
      padding: "1rem 0",
      borderBottom: "0.5px solid var(--color-border-tertiary)",
      animation: `fadeSlide 0.3s ease both`,
      animationDelay: `${rowIdx * 40}ms`,
    }}>
      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Number badge */}
      <div style={{
        minWidth: "52px",
        height: "52px",
        borderRadius: "50%",
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-secondary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        fontWeight: 500,
        color: "var(--color-text-primary)",
        flexShrink: 0,
        letterSpacing: "-0.02em",
      }}>
        {n}
      </div>

      {/* Pills */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        paddingTop: "10px",
      }}>
        {items.map((item, i) => {
          const palette = PILL_PALETTES[i % PILL_PALETTES.length];
          return (
            <span
              key={i}
              style={{
                display: "inline-block",
                padding: "3px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontFamily: "'Courier New', monospace",
                fontWeight: 500,
                letterSpacing: "0.03em",
                background: palette.bg,
                color: palette.text,
                border: `0.5px solid ${palette.border}`,
                whiteSpace: "nowrap",
                animation: `fadeSlide 0.25s ease both`,
                animationDelay: `${i * 18 + 60}ms`,
              }}
            >
              {item}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────
const labelStyle = {
  fontSize: "12px",
  letterSpacing: "0.06em",
  color: "var(--color-text-tertiary)",
  fontFamily: "'Courier New', monospace",
};

const numInputStyle = {
  width: "72px",
  padding: "6px 10px",
  fontSize: "14px",
  textAlign: "center",
  fontFamily: "'Courier New', monospace",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  outline: "none",
};

const navBtnStyle = {
  padding: "6px 14px",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "'Courier New', monospace",
  fontSize: "12px",
  letterSpacing: "0.04em",
};